import { useMemo, useRef, useState } from 'react'
import { useClienteAnalytics } from '../../hooks/useClienteAnalytics'
import { supabase } from '../../lib/supabase'
import KpiCard from './KpiCard'
import BarChart from './charts/BarChart'
import Heatmap from './charts/Heatmap'
import Alert from '../ui/Alert'
import { useToast } from '../../hooks/useToast'
import { fmtBRL, fmtNum, fmtPct } from '../../lib/format'
import { kpiMeta } from '../../lib/kpiMeta'

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

interface Props { windowMonths?: number }

interface SegmentCustomer {
  customer_id: string
  name: string
  segment: string
  orders: number
  monetary: number
  recency_days: number
  last_purchase: string
}

const numv = (v: unknown): number => (v == null ? 0 : Number(v))

const MONTH_ABBR = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function cohortLabel(iso: string): string {
  const d = new Date(iso)
  return `${MONTH_ABBR[d.getUTCMonth() + 1]}/${String(d.getUTCFullYear()).slice(2)}`
}

const SEGMENT_COLOR: Record<string, string> = {
  'Campeões': '#34d399',
  'Leais': '#22d3ee',
  'Novos/Promissores': '#a78bfa',
  'Atenção': '#fbbf24',
  'Em risco': '#f97316',
  'Hibernando': '#ef4444',
}

export default function ClientePanel({ windowMonths = 12 }: Props) {
  const { data, loading, error } = useClienteAnalytics(windowMonths)
  const { notify } = useToast()

  // Card "Em risco/hibernando" → lista de clientes desses segmentos (RPC sob demanda).
  const [segOpen, setSegOpen] = useState(false)
  const [segRows, setSegRows] = useState<SegmentCustomer[]>([])
  const [segLoading, setSegLoading] = useState(false)
  const segRef = useRef<HTMLDivElement>(null)

  async function toggleAtRisk() {
    const next = !segOpen
    setSegOpen(next)
    if (!next) return
    setSegLoading(true)
    const { data: rows, error: err } = await supabase.rpc('fn_customer_list_by_segment', {
      p_segments: ['Em risco', 'Hibernando'],
      p_months: windowMonths,
    })
    setSegLoading(false)
    if (err) { notify('error', `Erro ao listar clientes: ${err.message}`); return }
    setSegRows(((rows ?? []) as Record<string, unknown>[]).map(r => ({
      customer_id: String(r.customer_id),
      name: String(r.name ?? ''),
      segment: String(r.segment ?? ''),
      orders: numv(r.orders),
      monetary: numv(r.monetary),
      recency_days: numv(r.recency_days),
      last_purchase: String(r.last_purchase ?? ''),
    })))
    segRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const cohort = useMemo(() => {
    if (!data || data.cohort.length === 0) return null
    const months = [...new Set(data.cohort.map(c => c.cohort_month))].sort()
    const maxPeriod = Math.max(0, ...data.cohort.map(c => c.period_index))
    const periods = Array.from({ length: maxPeriod + 1 }, (_, i) => i)

    const map = new Map<string, Map<number, number>>()
    for (const c of data.cohort) {
      if (!map.has(c.cohort_month)) map.set(c.cohort_month, new Map())
      map.get(c.cohort_month)!.set(c.period_index, c.customers)
    }

    const values = months.map(m => {
      const row = map.get(m)!
      const base = row.get(0) ?? 0
      return periods.map(p => {
        const v = row.get(p)
        if (v == null) return null
        return base > 0 ? (v / base) * 100 : null
      })
    })
    return { rows: months.map(cohortLabel), cols: periods, values }
  }, [data])

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
  }
  if (error) return <Alert type="error" message={error} />
  if (!data) return null

  const k = data.kpis
  const atRisk = data.rfm.filter(s => s.segment === 'Em risco' || s.segment === 'Hibernando').reduce((a, b) => a + b.customers, 0)

  if (!k || k.total_customers === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-2">
        <p className="text-ink-muted text-sm font-medium">Ainda não há clientes vinculados às vendas.</p>
        <p className="text-ink-faint text-xs">Rode o sync/backfill para popular os dados de cliente.</p>
      </div>
    )
  }

  // Metas dos KPIs (régua única → tom do card + selo). Limiares vêm do texto "analysis".
  const recompraMeta = kpiMeta({ value: k.repeat_rate, sentido: 'min', verde: 40, amarelo: 20, format: v => `${v}%` })
  const atRiskPct = k.total_customers > 0 ? (atRisk / k.total_customers) * 100 : null
  const atRiskMeta = kpiMeta({ value: atRiskPct, sentido: 'max', verde: 15, amarelo: 30, format: v => `${v}%` })

  return (
    <div className="flex flex-col gap-6">
      {/* ── KPIs ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 [&>*]:w-[220px]">
        {/* Clientes/LTV: cards informativos (contagem/valor médio, sem meta) → sem selo. */}
        <KpiCard
          label="Clientes"
          value={fmtNum(k.total_customers)}
          tone="info"
          hint={`${fmtNum(k.active_customers)} ativos (${windowMonths}m)`}
          analysis="Total de clientes com compra; 'ativos' compraram dentro da janela. Base das demais métricas."
        />
        <KpiCard
          label="LTV médio"
          value={fmtBRL(k.avg_ltv)}
          tone="success"
          hint="valor médio por cliente"
          analysis="Valor médio gasto por cliente no período. Maior = base mais valiosa; cresça com recompra e ticket."
        />
        <KpiCard
          label="Taxa de recompra"
          value={fmtPct(k.repeat_rate)}
          tone={recompraMeta.tone}
          meta={{ status: recompraMeta.status, label: recompraMeta.metaLabel }}
          hint="clientes com +1 pedido"
          analysis="% de clientes com mais de um pedido. ≥40% bom · 20–40% atenção · <20% baixo (fidelização fraca)."
        />
        <KpiCard
          label="Em risco / hibernando"
          value={fmtNum(atRisk)}
          tone={atRiskMeta.tone}
          meta={{ status: atRiskMeta.status, label: atRiskMeta.metaLabel }}
          hint="clique para listar"
          onClick={toggleAtRisk}
          active={segOpen}
          analysis="Clientes de baixa recência. Acima de ~30% da base acende alerta; clique para listar e reativar."
        />
      </div>

      {/* Lista de clientes em risco/hibernando (card clicável) */}
      {segOpen && (
        <section ref={segRef} className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
              Clientes em risco / hibernando
            </h3>
            <button
              onClick={() => setSegOpen(false)}
              className="text-[11px] px-2 py-0.5 rounded bg-sunken hover:bg-line text-ink-muted border border-line"
            >
              Limpar
            </button>
          </div>
          {segLoading ? (
            <p className="text-sm text-ink-faint">Carregando...</p>
          ) : segRows.length === 0 ? (
            <p className="text-sm text-emerald-400">Nenhum cliente nesses segmentos.</p>
          ) : (
            <div className="overflow-y-auto max-h-[420px]">
              <table className="w-full text-sm mobile-stack-table">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                    <th className="text-left font-medium pb-2">Cliente</th>
                    <th className="text-left font-medium pb-2">Segmento</th>
                    <th className="text-right font-medium pb-2">Pedidos</th>
                    <th className="text-right font-medium pb-2">Valor total</th>
                    <th className="text-right font-medium pb-2">Sem comprar</th>
                    <th className="text-right font-medium pb-2 pr-3">Última compra</th>
                  </tr>
                </thead>
                <tbody>
                  {segRows.map(c => (
                    <tr key={c.customer_id} className="border-b border-line/50 last:border-0">
                      <td className="py-1.5 pr-3 text-ink max-w-[260px] truncate" title={c.name}>{c.name || '—'}</td>
                      <td className="py-1.5 pr-3">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ backgroundColor: (SEGMENT_COLOR[c.segment] ?? '#64748b') + '33', color: SEGMENT_COLOR[c.segment] ?? '#94a3b8' }}>
                          {c.segment}
                        </span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-ink-muted">{fmtNum(c.orders)}</td>
                      <td className="py-1.5 text-right tabular-nums text-emerald-300 font-semibold">{fmtBRL(c.monetary)}</td>
                      <td className="py-1.5 text-right tabular-nums text-orange-400">{fmtNum(c.recency_days)}d</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-ink-muted">{fmtDate(c.last_purchase)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Cohort de retenção */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Retenção por cohort</h3>
          {cohort ? (
            <Heatmap
              rows={cohort.rows}
              cols={cohort.cols}
              values={cohort.values}
              max={100}
              formatValue={v => `${Math.round(v)}%`}
            />
          ) : <p className="text-sm text-ink-faint">Sem dados de cohort.</p>}
          <p className="text-[11px] text-ink-faint">% de clientes da safra (mês da 1ª compra) ativos N meses depois</p>
        </section>

        {/* Segmentos RFM */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Segmentos RFM</h3>
          {data.rfm.length > 0 ? (
            <BarChart
              horizontal
              bars={data.rfm.map(s => ({
                label: s.segment,
                value: s.customers,
                color: SEGMENT_COLOR[s.segment] ?? '#64748b',
              }))}
              formatValue={v => fmtNum(v)}
            />
          ) : <p className="text-sm text-ink-faint">Sem dados de RFM.</p>}
          <p className="text-[11px] text-ink-faint">Clientes por segmento (recência × frequência × valor)</p>
        </section>
      </div>

      {/* ── Top clientes ──────────────────────────────────────── */}
      <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Top clientes por valor</h3>
        {data.top.length === 0 ? (
          <p className="text-sm text-ink-faint">Sem dados.</p>
        ) : (
          <table className="w-full text-sm mobile-stack-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-ink-faint">
                <th className="text-left font-medium pb-2">Cliente</th>
                <th className="text-right font-medium pb-2">Pedidos</th>
                <th className="text-right font-medium pb-2">Valor total</th>
                <th className="text-right font-medium pb-2 pr-3">Última compra</th>
              </tr>
            </thead>
            <tbody>
              {data.top.map(c => (
                <tr key={c.customer_id} className="border-b border-line/50 last:border-0">
                  <td className="py-1.5 pr-3 text-ink max-w-[260px] truncate" title={c.name}>{c.name || '—'}</td>
                  <td className="py-1.5 text-right tabular-nums text-ink-muted">{fmtNum(c.orders)}</td>
                  <td className="py-1.5 text-right tabular-nums text-emerald-300 font-semibold">{fmtBRL(c.monetary)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-ink-muted">{fmtDate(c.last_purchase)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
